import Phaser from 'phaser';
import { SCENE, GAME_WIDTH } from '../config.js';
import { api } from '../api/client.js';
import { gameState } from '../state/gameState.js';
import { showToast } from '../ui/Toast.js';
import { messageFor } from '../api/errors.js';
import { iconText } from '../ui/iconText.js';
import { quickLogins } from '../auth/quickLogin.mjs';
import { friendPortrait } from '../ui/friendPortrait.js';
import { openLookPanel } from '../ui/LookPanel.js';

const INPUT_CSS =
  'width: 300px; padding: 12px 16px; font-size: 22px; border-radius: 8px; ' +
  'border: 2px solid #ffd166; text-align: center; font-family: system-ui;';

export class LoginScene extends Phaser.Scene {
  constructor() { super(SCENE.LOGIN); }

  create(data) {
    this.mode = 'login'; // 'login' | 'register'
    // Phaser reuses this scene object: a quick login that went through left _busy set, so after the
    // next logout every circle tap was ignored until a page reload (owner 2026-10-16).
    this._busy = false;
    this.cameras.main.setBackgroundColor('#2d1b69');

    // Gameplay scenes' WalkControllers register global keyboard captures (W/A/S/D +
    // arrows) that call preventDefault. Those captures persist after logout, so without
    // this the login DOM inputs never receive those letters (you can't type "a"). Login
    // needs no key capture — clear them so every key reaches the text fields.
    this.input.keyboard.clearCaptures();

    // Remembered accounts (quick login, owner 2026-10-03): a row of circles above the form, which moves down for them.
    const remembered = quickLogins.list();
    const dy = remembered.length ? 70 : 0;
    iconText(this, GAME_WIDTH / 2, remembered.length ? 70 : 130, '{moon} Stary Dom', {
      fontFamily: 'system-ui', fontSize: '64px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(0.5);

    this.title = iconText(this, GAME_WIDTH / 2, 230 + dy, '', {
      fontFamily: 'system-ui', fontSize: '28px', color: '#fff',
    }).setOrigin(0.5);

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.placeholder = 'Imię';
    nameInput.maxLength = 50;
    nameInput.style.cssText = INPUT_CSS;
    const nameDom = this.add.dom(GAME_WIDTH / 2, 300 + dy, nameInput).setOrigin(0.5);

    const pwInput = document.createElement('input');
    pwInput.type = 'password';
    pwInput.placeholder = 'Hasło';
    pwInput.maxLength = 128;
    pwInput.style.cssText = INPUT_CSS;
    const pwDom = this.add.dom(GAME_WIDTH / 2, 370 + dy, pwInput).setOrigin(0.5);

    // Character choice — only shown when registering (permanent for the account); then the look (Wygląd).
    this.character = 'natalka';
    this._look = null;
    const mkChar = (x, val, label) => {
      const r = this.add.rectangle(x, 435 + dy, 180, 56, 0x3a2a7a)
        .setStrokeStyle(3, 0x555555).setInteractive({ useHandCursor: true });
      const t = iconText(this, x, 435 + dy, label, { fontFamily: 'system-ui', fontSize: '20px', color: '#fff' }).setOrigin(0.5);
      r.on('pointerup', () => { if (this.character !== val) this._look = null; this.character = val; this._renderCharChoice(); });
      return { r, t, val };
    };
    this.charBtns = [
      mkChar(GAME_WIDTH / 2 - 100, 'natalka', '{girl} Dziewczynka'),
      mkChar(GAME_WIDTH / 2 + 100, 'ken', '{boy} Chłopiec'),
    ];

    const btn = this.add.rectangle(GAME_WIDTH / 2, 515 + dy, 260, 60, 0xffd166)
      .setInteractive({ useHandCursor: true });
    btn.setStrokeStyle(3, 0xffffff);
    this.btnLabel = iconText(this, GAME_WIDTH / 2, 515 + dy, '', {
      fontFamily: 'system-ui', fontSize: '28px', color: '#000', fontStyle: 'bold',
    }).setOrigin(0.5);

    this.toggle = iconText(this, GAME_WIDTH / 2, 585 + dy, '', {
      fontFamily: 'system-ui', fontSize: '20px', color: '#ffd166',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    const toggleMode = () => {
      this.mode = this.mode === 'login' ? 'register' : 'login';
      this._applyMode();
    };
    this.toggle.on('pointerup', toggleMode);
    // A finger-sized target around the link: on a phone its text is ~9 px tall.
    this.add.rectangle(GAME_WIDTH / 2, 585 + dy, 440, 60, 0x000000, 0.001).setDepth(-1)
      .setInteractive({ useHandCursor: true }).on('pointerup', toggleMode);

    const submit = async () => {
      const name = nameInput.value.trim();
      const password = pwInput.value;
      if (!name) { showToast(this, 'Wpisz imię'); return; }
      if (password.length < 6) { showToast(this, 'Hasło: min. 6 znaków'); return; }
      btn.disableInteractive();
      if (this.mode === 'register') {
        // Wygląd before the account exists (character look): the form's DOM fields sit over the canvas, so they hide
        const fields = [nameDom, pwDom];
        const back = () => { for (const f of fields) f.setVisible(true); btn.setInteractive({ useHandCursor: true }); };
        for (const f of fields) f.setVisible(false);
        openLookPanel(this, {
          character: this.character, look: this._look, onCancel: back,
          onDone: async (look) => {
            this._look = look;
            try {
              const res = await api.register(name, password, this.character, look);
              quickLogins.remember(res);
              quickLogins.setLook(res.player_id, { character: res.character, equipped: null, look: res.look ?? null });
              this.scene.start(SCENE.BOOT);
            } catch (e) { showToast(this, messageFor(e)); throw e; }
          },
        });
        return;
      }
      try {
        const res = await api.login(name, password);
        quickLogins.remember(res);                 // her circle on this device from now on
        // Re-run BootScene's post-auth bootstrap (__myId/__myCharacter, gameState,
        // realtime.connect, outfit broadcast). Starting MAIN directly skipped all of it,
        // so a fresh login rendered the default girl avatar and never opened the WS.
        this.scene.start(SCENE.BOOT);
      } catch (e) {
        showToast(this, messageFor(e));
        btn.setInteractive({ useHandCursor: true });
      }
    };

    btn.on('pointerup', submit);
    pwInput.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') submit(); });
    nameInput.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') pwInput.focus(); });

    this._applyMode();
    if (data?.note) showToast(this, data.note, 3500);      // sent here because the login ran out
    if (remembered.length) this._drawRemembered(remembered);
    else setTimeout(() => nameInput.focus(), 100);
  }

  // One circle per remembered account: her character in her last look, her name under it, a ✕ to forget her here.
  _drawRemembered(list) {
    // y: the same ~22 px gap from the logo to the circle as from the name to the form's title (owner 2026-10-03)
    const R = 46, STEP = 150, y = 182;
    const shown = list.slice(0, 6);
    shown.forEach((e, i) => {
      const x = GAME_WIDTH / 2 + (i - (shown.length - 1) / 2) * STEP;
      const ring = this.add.circle(x, y, R + 4, 0xffd166).setInteractive({ useHandCursor: true });
      this.add.circle(x, y, R, 0x5b3fa0);
      const fallback = iconText(this, x, y, e.character === 'ken' ? '{boy}' : '{girl}', { fontSize: '44px' }).setOrigin(0.5);
      const label = iconText(this, x, y + R + 22, e.name, { fontFamily: 'system-ui', fontSize: '20px', color: '#fff', fontStyle: 'bold' }).setOrigin(0.5);
      for (const px of [18, 16, 14, 12]) { if (label.width <= STEP - 14) break; label.setFontSize(px); }   // a long name fits under its circle
      friendPortrait(this, { character: e.character, outfit: { equipped: e.equipped, look: e.look ?? null } }).then((url) => {
        if (!url || !this.sys.isActive()) return;
        const img = new Image();
        img.onload = () => {
          if (!this.sys.isActive()) return;
          const key = `quick_face_${e.player_id}_${Date.now()}`;
          this.textures.addImage(key, img);
          const face = this.add.image(x, y, key).setDisplaySize(2 * R, 2 * R);
          const m = this.make.graphics({ add: false }); m.fillStyle(0xffffff).fillCircle(x, y, R);
          face.setMask(m.createGeometryMask());
          fallback.destroy();
        };
        img.src = url;
      });
      const login = async () => {
        if (this._busy) return; this._busy = true;
        try { await api.quickLogin(e.token); this.scene.start(SCENE.BOOT); }
        catch (err) {
          // revoked elsewhere or expired: forget it here too and let her type the password
          if (err?.status === 401) { quickLogins.forget(e.player_id); showToast(this, 'Zaloguj się hasłem'); this.time.delayedCall(1200, () => this.scene.restart()); }
          else showToast(this, messageFor(err));
          this._busy = false;
        }
      };
      ring.on('pointerup', login);
      // the cross: forget her on this device and revoke the key on the server
      const xBtn = this.add.circle(x + R * 0.78, y - R * 0.78, 15, 0xe0604d).setStrokeStyle(2, 0xffffff).setInteractive({ useHandCursor: true });
      iconText(this, xBtn.x, xBtn.y, '{cross}', { fontFamily: 'system-ui', fontSize: '16px', color: '#fff', fontStyle: 'bold' }).setOrigin(0.5);
      xBtn.on('pointerup', (p, lx, ly, ev) => {
        ev?.stopPropagation?.();
        const gone = quickLogins.forget(e.player_id);
        if (gone?.token) api.quickForget(gone.token).catch(() => {});
        this.scene.restart();
      });
    });
  }

  _applyMode() {
    const isLogin = this.mode === 'login';
    this.title.setText(isLogin ? 'Zaloguj się' : 'Załóż konto');
    this.btnLabel.setText(isLogin ? 'Zaloguj się {gamepad}' : 'Załóż konto {sparkle}');
    this.toggle.setText(isLogin ? 'Nie masz konta? Załóż je' : 'Masz już konto? Zaloguj się');
    for (const b of this.charBtns) { b.r.setVisible(!isLogin); b.t.setVisible(!isLogin); }
    this._renderCharChoice();
  }

  _renderCharChoice() {
    for (const b of this.charBtns) {
      const sel = b.val === this.character;
      b.r.setStrokeStyle(3, sel ? 0xffd166 : 0x555555).setFillStyle(sel ? 0x5b3fa0 : 0x3a2a7a);
    }
  }
}
